import React, { useRef, useEffect, useState } from "react";
import styles from './GraduationTimeline.module.scss';
import PropTypes from "prop-types";

const GraduationTimeline = ({ data = [], minimumScore = 0 }) => {

  const timelineData = data.length > 0 ? data : [];
  const nodeRefs = useRef([]);
  const containerRef = useRef(null);
  const [lines, setLines] = useState([]);
  const parsedMinimumScore = parseFloat(minimumScore) || 0;

  // Determine colors for segments based on node status
  const line1Color = timelineData.length > 0 && timelineData[0]?.status === 'Completed' ? '#00B828' : '#8C8C8C';
  const line2Color = timelineData.length > 1 && timelineData[1]?.status === 'Completed' ? '#00B828' : '#8C8C8C';

  // Calculate marker line position based on minimum score (out of 5)
  const markerX = (parsedMinimumScore / 5) * 165;

  // Measure and update connector lines
  useEffect(() => {
    function updateLines() {
      if (nodeRefs.current.length < 2 || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newLines = [];

      for (let i = 0; i < nodeRefs.current.length - 1; i++) {
        const nodeA = nodeRefs.current[i];
        const nodeB = nodeRefs.current[i + 1];

        if (!nodeA || !nodeB) continue;

        const rectA = nodeA.getBoundingClientRect();
        const rectB = nodeB.getBoundingClientRect();

        // Calculate coordinates relative to the container
        newLines.push({
          x1: rectA.left - containerRect.left + rectA.width / 2,
          y1: rectA.top - containerRect.top + rectA.height / 2,
          x2: rectB.left - containerRect.left + rectB.width / 2,
          y2: rectB.top - containerRect.top + rectB.height / 2,
          color: i === 0 ? line1Color : line2Color,
        });
      }

      setLines(newLines);
    }

    updateLines();
    window.addEventListener("resize", updateLines);
    window.addEventListener("scroll", updateLines);

    return () => {
      window.removeEventListener("resize", updateLines);
      window.removeEventListener("scroll", updateLines);
    };
  }, [timelineData.length, line1Color, line2Color]);

  return (
    <div className={styles.graduationTimelineBox}>
      <h2 className={styles.title}>Graduation Timeline</h2>
      <div className={styles.timelineContainer} ref={containerRef} style={{ position: "relative" }}>
        {/* SVG overlay for connector lines */}
        {lines.length > 0 && (
          <svg
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              zIndex: 0,
              overflow: "visible",
            }}
          >
            {lines.map((line, index) => (
              <line
                key={index}
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
                stroke={line.color}
                strokeWidth="2"
              />
            ))}
          </svg>
        )}
        <div className={styles.timelineItems}>
          {timelineData.map((item, index) => (
            <div key={index} className={styles.timelineItem}>
              <div 
                ref={(el) => { nodeRefs.current[index] = el; }}
                className={`${styles.timelineNode} ${styles[item.statusClass]}`}
              ></div>
              <div className={styles.timelineContent}>
                <div className={styles.yearStatus}>
                  <h4>{item.year}</h4>
                  <p className={`${styles.status} ${styles[item.statusClass]}`}>{item.status}</p>
                </div>
                <div className={styles.scoreInfo}>
                  <span className={styles.scoreLabel}>Cumulative Score</span>
                  <span className={styles.scoreValue}>{item.cumulativeScore}</span>
                  <svg width="165" height="24" viewBox="0 0 165 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <text x="0" y="22" fontSize="12" fill="#C6C6C6">0</text>
                    <text x={markerX - 5} y="22" fontSize="12" fill="#172B4D">{parsedMinimumScore}</text>
                    <text x="155" y="22" fontSize="12" fill="#C6C6C6">5</text>
                    <rect y="3" width="165" height="5" rx="2.5" fill="#E4E4E4"/>
                    <rect y="3" width={Math.min((item.cumulativeScore / 5) * 165, 165)} height="5" rx="2.5" fill={item.cumulativeScore < parsedMinimumScore ? '#FF4646' : '#00B828'}/>
                    <line x1={markerX} y1="1.73315e-08" x2={markerX} y2="11" stroke="black"/>
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

GraduationTimeline.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    year: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    statusClass: PropTypes.string.isRequired,
    cumulativeScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    scorePercentage: PropTypes.number,
    scoreColor: PropTypes.string,
  })),
  minimumScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default GraduationTimeline;

